import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CellClickedEvent,
  ColDef,
  GetDataPath,
  GridApi,
  IAggFunc,
  IAggFuncParams,
  SideBarDef,
  IRowNode,
  ColumnMenuTab,
} from "ag-grid-community";
import Select from "react-select";
import { useNavigate } from "react-router";
import { AgGridReact } from "ag-grid-react";
import { Box, Dialog, Slider } from "@mui/material";
import _ from "lodash";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  tableCellStyle,
  tableEntryStyle,
} from "../../components/TableComponent";
import SearchInput from "../../components/shared/SearchInput";
import { useGetCompanyName, HStack, VStack } from "../../components/utils";
import { useAuthStore } from "../../store";
import { TimeLineRenderer } from "../finance/components/tables/BalanceSheetAGComponent";
import { IconCTAButton } from "../quickRound/CTAButtonComponents";
import { agConfigDashboardSideBar } from "./AgGridConfig";
import {
  CustomToolTip,
  CustomCaptableTooltipComponent,
  RoundsToolTip,
  financeCustomToolTip,
} from "./customTooltip";
import { RoundModel, SubsidiaryCompanyDetails } from "../../types/CapTable";
import { DashBoardModel, CumulativeHolding } from "../../types/DashboardModel";
import { useCapTableFilterStore } from "../../store/capTableFilterStore";
import { useSelectedShareholderFilterStore } from "../../store/selectedShareholderTypeStore";
import {
  exportSecurityBasedReport,
  exportRoundReport,
  exportCaptableSummaryReport,
} from "../../api/Report";
import {
  getScenarioCaptableSummaryReport,
  getOwnershipOverViewReport,
} from "../../api/Reports";
import { filterString, sliderStyle } from "../../constants/DashboardConstants";
import {
  shareholderOrder,
  getShareholderValue,
  getShareholderTagCustomColor,
} from "../../constants/ShareholderConstants";
import {
  useGetSubsidiaryCompanies,
  useGetSubsidiaryCompanyCaptable,
} from "../../queries/captable";
import { useAgGridTableState } from "../../store/agGridTableStore";
import { GenericCumulativeHolding } from "../../types/GenericCaptableModel";
import {
  downloadExcel,
  convertBase64ToBlob,
  downloadBlobObject,
} from "../../utils/DownloadFile";
import { globalFilter, sort } from "../../utils/arrays";
import { _trimAll, classNames, limitString } from "../../utils/string";
import { useGetOnFilterState, usePostOnFilterState } from "./AgGridCacheQuery";
import ExportMultidateSheetDetails from "../../modals/ExportMultidateSheetDetails";
import GenericTableHeader from "../../shared/TableHeader";
import { formatToReadableNumber } from "../../utils/currencyRoboto";
import { formatDisplayDate } from "../../utils/date";
import { ExportImport } from "../Utility/GrantsTable";
import { Action } from "../../components/shared/Dropdown";
import Tooltip from "../../components/shared/Tooltip";
import {
  AmountInvestedRender,
  DebenturesRender,
  EquitySharesRender,
  FdbPercentageRender,
  FdbSharesRender,
  NotesRender,
  OptionsRender,
  PreferenceShareRender,
  RoundsRender,
  TotalInvestedAmountRender,
  WarrantsRender,
  shareholderNameRender,
} from "./AgGridCaptableComponent";
import { handleEventForTracking } from "../../amplitudeAnalytics";
import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import { SelectOptionType } from "../../types/BoardMeetingsModels";
import { useTableStateManagement } from "../rounds/TableHook";
import { TableId } from "../../constants/TableIdConstants";
import CloudSetting from "../../components/shared/CloudIcon";
import { CompanyStages } from "../../store/useRoleGrantStore";
import {
  getDisplayedRounds,
  getSelectedRoundNames,
  projectSharesForRounds,
} from "../../utils/roundShareProjection";

interface MarkLabel {
  value: number;
  label: string;
}
export interface ShareholderTypeTotal {
  name: string;
  value: number;
}

function AgGridDashboardCaptable({
  cinNumber,
  allEventsDetail = [],
  isScenarioCaptable = false,
  companyId,
  markLabels,
  latestCaptable,
  roundModel,
}: {
  cinNumber?: string;
  allEventsDetail?: DashBoardModel[];
  isScenarioCaptable?: boolean;
  companyId: string;
  markLabels: MarkLabel[];
  latestCaptable: CumulativeHolding[];
  roundModel?: RoundModel;
}) {
  const gridApi = useRef<any>(null);
  const gridRef = useRef<AgGridReact>(null);
  const length = (allEventsDetail?.length || 1) - 1;
  const [captableData, setCapTableData] = useState<CumulativeHolding[]>([]);
  const [isSubsidiary, setIsSubsidiary] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);
  const [subsidiaryCompanyId, setSubsidiaryCompanyId] = useState<string>("");
  const [subsidiaryCompanies, setSubsidiaryCompanies] =
    useState<SubsidiaryCompanyDetails[]>();
  let _captableData: GenericCumulativeHolding[] =
    captableData.length > 0 ? captableData : latestCaptable;
  const tableViewOptions = [
    { label: "All", value: 0 },
    { label: "Equity", value: 1 },
    { label: "Debt", value: 2 },
  ];
  const [tableViewValue, setTableViewValue] = useState<SelectOptionType>(
    tableViewOptions[0]
  );
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [selectedRoundNames, setSelectedRoundNames] = useState<string[]>([]);

  function customSort(arr: CumulativeHolding[]): CumulativeHolding[] {
    const order: { [key: string]: number } = shareholderOrder;
    arr.sort((a, b) => {
      const aIndex = order[(a.type || "").toLowerCase()] || Number.MAX_VALUE;
      const bIndex = order[(b.type || "").toLowerCase()] || Number.MAX_VALUE;
      return aIndex - bIndex;
    });
    return arr;
  }

  function customTotalSort(
    arr: ShareholderTypeTotal[]
  ): ShareholderTypeTotal[] {
    const order: { [key: string]: number } = shareholderOrder;
    arr.sort((a, b) => {
      const aIndex = order[(a.name || "").toLowerCase()] || Number.MAX_VALUE;
      const bIndex = order[(b.name || "").toLowerCase()] || Number.MAX_VALUE;
      return aIndex - bIndex;
    });
    return arr;
  }

  const [pageLoaded, setPageLoaded] = useState(false);
  const {
    filterCondition,
    setFilterCondition,
    isSaving,
    uploadFilter,
    uploadColumn,
    tableState,
  } = useTableStateManagement(
    gridApi,
    `${TableId.dashboardCaptable}${companyId}web`,
    pageLoaded
  );

  const currency = getCurrencyType();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalChart, setTotalChart] = useState<{
    name: string;
    type: string;
  }>({
    name: "FDB Shares",
    type: "fdbShares",
  });
  const [totalValue, setTotalValue] = useState<ShareholderTypeTotal[]>([]);
  const [checkIsDataAvailable, setCheckIsDataAvailable] = useState(false);
  const [dialog, setDialog] = useState<{
    open: boolean;
    data?: string;
    type?: string;
  }>({
    open: false,
  });

  const capTablefilterStore = useCapTableFilterStore();
  const [open, setOpen] = useState(false);

  const { data: subsidiaryCompaniesList } = useGetSubsidiaryCompanies();
  const {
    refetch: getSubsidiaryCompanyCaptable,
    data: subsidiaryCompanyCaptable,
  } = useGetSubsidiaryCompanyCaptable(subsidiaryCompanyId || "");

  useEffect(() => {
    if (allEventsDetail.length > 1) {
      capTablefilterStore.reset();
      updateRange(length);
      capTablefilterStore.setRound(allEventsDetail[length].roundSummary.name);
      capTablefilterStore.setDateOfInvestment(
        new Date(allEventsDetail[length].eventDate)
      );
    }
    setCount(allEventsDetail?.length || 0 - 1);
  }, [allEventsDetail]);

  useEffect(() => {
    if (subsidiaryCompaniesList && Array.isArray(subsidiaryCompaniesList))
      setSubsidiaryCompanies(subsidiaryCompaniesList);
  }, [subsidiaryCompaniesList]);

  useEffect(() => {
    if (subsidiaryCompanyCaptable) {
      setCapTableData(customSort(subsidiaryCompanyCaptable));
      setPageLoaded(true);
    }
  }, [subsidiaryCompanyCaptable]);

  const updateRange = (countNumber: any) => {
    if (allEventsDetail) {
      setCount(countNumber);
      setCapTableData(customSort(allEventsDetail[countNumber].capTable));
      setPageLoaded(true);
    }
  };

  _captableData = useMemo(() => {
    try {
      if (!_captableData) return [];
      const filterResult = globalFilter(_captableData, "", filterString)
        .filter(
          (shareHolder) =>
            !capTablefilterStore.typeOfShareHolder ||
            _trimAll((shareHolder.type || "").toLowerCase()) ===
              _trimAll(capTablefilterStore.typeOfShareHolder.toLowerCase())
        )
        .filter(
          (shareHolder) =>
            !capTablefilterStore.actionableInsightFilters[0].enabled ||
            (shareHolder.fdbPercentage || 0) > 0.05
        )
        .filter(
          (shareHolder) =>
            !capTablefilterStore.actionableInsightFilters[1].enabled ||
            shareHolder.residential !== "India"
        )
        .filter(
          (shareHolder) =>
            capTablefilterStore.shareholder.includes(shareHolder.type || "") ||
            capTablefilterStore.shareholder.length === 0
        );
      return filterResult;
    } catch (e) {
      return _captableData;
    }
  }, [_captableData, capTablefilterStore, latestCaptable]);

  const companyName = useGetCompanyName();
  let fileName;
  async function handleActions(action: Action) {
    if (action.disabled) return;
    if (action.name === "Security Based Report") {
      await exportSecurityBasedReport("").then((res) => {
        fileName = `Security Based Report_${companyName}`;
        downloadExcel(res, fileName);
      });
    } else if (action.name === "Round Based Report") {
      await exportRoundReport("").then((res) => {
        fileName = `Round Based Report_${companyName}`;
        downloadExcel(res, fileName);
      });
    } else if (action.name === "Export Summary Report") {
      if (roundModel) {
        getScenarioCaptableSummaryReport(
          companyId,
          roundModel.id || "",
          true
        ).then((data) => {
          const fileName = `${roundModel.name} scenario captable`;
          convertBase64ToBlob(
            data.data,
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          ).then((downloadData) => {
            downloadBlobObject(downloadData, fileName);
          });
        });
        return;
      }
      setDialog({
        open: true,
        data: "Export Summary Report",
        type: "Export Summary Report",
      });
    } else if (action.name === "Allotment Window Report") {
      setDialog({
        open: true,
        data: "Allotment Window Report",
      });
    } else if (action.name === "Ownership Detailed Report") {
      await getOwnershipOverViewReport("").then((res) => {
        fileName = `Ownership Detailed Report_${companyName}`;
        downloadExcel(res, fileName);
      });
    }
  }

  useEffect(() => {
    const newCaptableData = captableData.map((element) => ({
      ...element,
      fdbPercentage: parseFloat(
        ((element.fdbPercentage || 0) * 100).toFixed(2)
      ),
    }));
    const total = _.groupBy(newCaptableData, "type");
    const totalVal: ShareholderTypeTotal[] = [];
    for (const investorType of Object.keys(total)) {
      const shareholderType: ShareholderTypeTotal = {
        name: investorType,
        value: _.sumBy(total[investorType], totalChart.type),
      };
      totalVal.push(shareholderType);
    }
    const sortedResults = sort(totalVal, "value", false);
    setTotalValue(customTotalSort(sortedResults));
    const count = totalVal.find((total) => total.value > 0);
    const countValue = (count?.name || "").length > 0;
    setCheckIsDataAvailable(countValue);
  }, [totalChart, _captableData]);

  const firstRoundMarkIndex = Math.floor((allEventsDetail.length - 1) / 4);
  const secondRoundMarkIndex = Math.floor((allEventsDetail.length - 1) / 2);
  const thirdRoundMarkIndex = Math.floor(
    ((allEventsDetail.length - 1) / 4) * 3
  );

  const currencySymbol = getCurrencySymbol();
  const agTableStore = useAgGridTableState();
  const userId = useAuthStore().user?.userId ?? 0;

  const displayedRowCount = gridApi.current?.api.getDisplayedRowCount();

  const { refetch } = useGetOnFilterState(`${userId}`, companyId);

  useEffect(() => {
    refetch().then((data) => {
      if (data.data) agTableStore.setState(data.data);
      setColumnSetting(data.data?.captableColumnModel);
      setFilterSetting(data.data?.captableFilterModel);
    });
  }, []);

  const { mutate: postOnFilter } = usePostOnFilterState();

  function getColumnSetting() {
    if (gridApi.current) return gridApi.current.columnApi?.getColumnState();
    return {};
  }

  function setColumnSetting(model: any) {
    if (gridApi.current)
      gridApi.current.columnApi.applyColumnState({ state: model });
    return {};
  }

  const getFilterSetting = () => {
    if (gridApi.current) return gridApi.current.api?.getFilterModel();
    return {};
  };

  const setFilterSetting = (model: any) => {
    if (gridApi.current) return gridApi.current.api?.setFilterModel(model);
    return {};
  };

  const uploadFilterAndColumn = async () => {
    const columnState = await getColumnSetting();
    const filterState = await getFilterSetting();
    postOnFilter({
      userId: `${userId}`,
      companyId,
      filterData: {
        ...agTableStore.state,
        captableColumnModel: columnState,
        captableFilterModel: filterState,
      },
    });
  };

  const componentsRegistery = useMemo(
    () => ({
      shareholderName: memo(shareholderNameRender),
      equity: memo(EquitySharesRender),
      roundIdentifiers: memo(RoundsRender),
      preference: memo(PreferenceShareRender),
      debentures: memo(DebenturesRender),
      amountInvested: memo(AmountInvestedRender),
      totalAmount: memo(TotalInvestedAmountRender),
      fdbShares: memo(FdbSharesRender),
      fdbPercentage: memo(FdbPercentageRender),
      warrants: memo(WarrantsRender),
      notes: memo(NotesRender),
      options: memo(OptionsRender)
    }),
    []
  );

  const navigate = useNavigate();

  const onClickShareholderName = (id: string) => {
    if (!cinNumber) {
      handleEventForTracking({ eventName: `Shareholder Name` });
      navigate(`/shareholders/individual/${id}`);
    }
  };

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      enableCellClick: true,
      autoHeight: true,
      wrapHeaderText: true,
      suppressColumnVirtualisation: true,
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 5,
      },
      minWidth: 150,
      filter: true,
      resizable: true,
      flex: 1,
    }),
    []
  );

  const { user: companyStage } = useAuthStore.getState();

  const columnDefs: ColDef[] = useMemo(() => {
    const columnDef: ColDef[] = [
      ...(companyStage?.stage === CompanyStages.THREE ? [] : [{
        headerName: "Rounds",
        field: "rounds",
        cellRenderer: "roundIdentifiers",
        initialWidth: 250,
        cellStyle: {
          "padding-top": "5px",
          "line-height": "20px",
        },
        sortable: false,
        filter: "agSetColumnFilter",
        valueGetter: (params) =>
          params.data.roundFilterValues ||
          params.data.rounds?.map((value: any) => value.roundName),
        valueFormatter: (params) =>
          params.data.rounds?.map((value: any) => value.roundName),
        menuTabs: ["filterMenuTab"],
        tooltipField: "roundsTooltipField",
        tooltipComponent: (params: any) => {
          if (params.data.orgHierarchy[0] !== "Total") {
            return <RoundsToolTip {...params} />;
          }
          return <></>;
        },
      } as ColDef]),
    ];

    const equityColumns: ColDef[] = [
      {
        headerName: "Equity",
        field: "equityShares",
        filter: "agNumberColumnFilter",
        cellRenderer: "equity",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipEquity",
        hide: false,
        tooltipComponent: (params: any) => (
          <CustomCaptableTooltipComponent {...params} />
        ),
      },
      {
        headerName: "Preference",
        field: "prefShares",
        filter: "agNumberColumnFilter",
        cellRenderer: "preference",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipPreference",
        hide: false,
        tooltipComponent: (params: any) => (
          <CustomCaptableTooltipComponent {...params} />
        ),
      },
      {
        headerName: "Warrants",
        field: "warrants",
        filter: "agNumberColumnFilter",
        cellRenderer: "warrants",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipWarrants",
        hide: true,
        tooltipComponent: (params: any) => (
          <CustomCaptableTooltipComponent {...params} />
        ),
      },
      {
        headerName: "Notes",
        field: "notes",
        filter: "agNumberColumnFilter",
        cellRenderer: "notes",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipNotes",
        hide: true,
        tooltipComponent: (params: any) => (
          <CustomCaptableTooltipComponent {...params} />
        ),
      },
      {
       headerName: "Options",
        field: "options",
        filter: "agNumberColumnFilter",
        cellRenderer: "options",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipOptions",
        hide: true,
      },
      {
        headerName: "FDB Shares",
        field: "fdbShares",
        filter: "agNumberColumnFilter",
        cellRenderer: "fdbShares",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipFdbShares",
        hide: false,
        tooltipComponent: (params: any) => (
          <CustomCaptableTooltipComponent {...params} />
        ),
      },
      ...(companyStage?.stage === CompanyStages.THREE ? [] : [{
        headerName: `Paid up Amount (${currencySymbol})`,
        field: "amountInvested",
        filter: "agNumberColumnFilter",
        cellRenderer: "amountInvested",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"] as ColumnMenuTab[],
        tooltipField: "tooltipAmountInvested",
        hide: true,
        tooltipComponent: (params: any) => (
          <CustomCaptableTooltipComponent {...params} />
        ),
      }]),
      {
        headerName: `FDB %`,
        field: "fdbPercentage",
        filter: "agNumberColumnFilter",
        cellRenderer: "fdbPercentage",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipFdbPercentage",
        hide: false,
        tooltipComponent: (params: any) => (
          <CustomCaptableTooltipComponent {...params} />
        ),
      },
    ];

    const debtColumn: ColDef[] = [
      {
        headerName: "Debentures",
        field: "debentures",
        filter: "agNumberColumnFilter",
        cellRenderer: "debentures",
        cellStyle: tableCellStyle,
        sortable: true,
        hide: tableViewValue.label !== "Debt",
        menuTabs: ["filterMenuTab"],
        tooltipField: "tooltipDebentures",
        tooltipComponent: (params: any) => (
          <CustomCaptableTooltipComponent {...params} />
        ),
      },
    ];

    const totalAmountColumn: ColDef[] = [
      ...(companyStage?.stage === CompanyStages.THREE ? [] : [
      {
        headerName: `Total Investment (${currencySymbol})`,
        field: "totalAmount",
        filter: "agNumberColumnFilter",
        cellRenderer: "totalAmount",
        cellStyle: tableCellStyle,
        sortable: true,
        menuTabs: ["filterMenuTab"] as ColumnMenuTab[],
        tooltipField: "tooltipTotalAmountInvested",
        hide: tableViewValue.label !== "Debt",
        tooltipComponent: (params: any) => (
          <CustomCaptableTooltipComponent {...params} />
        ),
      }]),
    ];

    if (tableViewValue.label === "All") {
      columnDef.splice(1, 0, ...equityColumns);
      columnDef.splice(3, 0, ...debtColumn);
      columnDef.splice(6, 0, ...totalAmountColumn);
    }
    if (tableViewValue.label === "Equity") {
      columnDef.splice(1, 0, ...equityColumns);
      columnDef.splice(6, 0, ...totalAmountColumn);
    }
    if (tableViewValue.label === "Debt") {
      columnDef.splice(1, 0, ...debtColumn);
      columnDef.splice(2, 0, ...totalAmountColumn);
    }
    return columnDef;
  }, [tableViewValue]);

  const [groupDefaultExpanded, setGroupDefaultExpanded] = useState(0);

  const currencyType = getCurrencyType();
  const pathName = window.location.href.split("/");
  const sharedPage = useMemo(() => pathName[3] === "share", [pathName]);

  const rowData = useMemo(() => {
    if (!_captableData) return [];

    const projectedCaptableData = _captableData.map((holding) => ({
      ...holding,
      ...projectSharesForRounds(holding, selectedRoundNames),
    }));
    const groupedByType = _.groupBy(projectedCaptableData, "type");

    const rows: any[] = [];

    Object.entries(groupedByType).forEach(([shareholderType, typeData]) => {
      const totalEquityShares = typeData.reduce(
        (acc: number, item: any) => acc + (item.equityShares || 0),
        0
      );
      const totalPrefShares = typeData.reduce(
        (acc: number, item: any) => acc + (item.prefShares || 0),
        0
      );
      const totalDebentures = typeData.reduce(
        (acc: number, item: any) => acc + (item.debentures || 0),
        0
      );
      const totalWarrants = typeData.reduce(
        (acc: number, item: any) => acc + (item.warrants || 0),
        0
      );
      const totalNotes = typeData.reduce(
        (acc: number, item: any) => acc + (item.notes || 0),
        0
      );
      const totalOptions = typeData.reduce(
         (acc: number, item: any) => acc + (item.options || 0),
        0
      );
      const totalFdbShares = typeData.reduce(
        (acc: number, item: any) => acc + (item.fdbShares || 0),
        0
      );
      const totalAmountInvested = typeData.reduce(
        (acc: number, item: any) => acc + (item.investedAmount || 0),
        0
      );
      const totalFdbPercentage = typeData.reduce(
        (acc: number, item: any) =>
          acc + Math.abs(item.fdbPercentage || 0) * 100,
        0
      );
      const totalTotalAmount = typeData.reduce(
        (acc: number, item: any) => acc + (item.totalAmount || 0),
        0
      );
      const roundIdentifiers: any = {};
      const groupedData = _.groupBy(projectedCaptableData, "type");

      for (const [groupType, groupData] of Object.entries(groupedData)) {
        const roundIdentifierDetails = _.uniqBy(groupData.flatMap(
          (item) => item.roundIdentifierDetails
        ), "roundName");

        roundIdentifiers[groupType.toLowerCase()] =
          tableViewValue.label === "Debt"
            ? roundIdentifierDetails.filter(
                (round: any) =>
                  round.instrumentTypes.includes("NCD") ||
                  round.instrumentTypes.includes("OCD")
              )
            : tableViewValue.label === "Equity"
            ? roundIdentifierDetails.filter(
                (round: any) =>
                  !round.instrumentTypes.includes("NCD") &&
                  !round.instrumentTypes.includes("OCD")
              )
            : roundIdentifierDetails;
        roundIdentifiers[groupType.toLowerCase()] = getDisplayedRounds(
          roundIdentifiers[groupType.toLowerCase()],
          selectedRoundNames
        );
      }

      const roundIdentifiersGroups: any = {};
      const groupedMainData = _.groupBy(projectedCaptableData, "groupName");

      for (const [groupName, groupData] of Object.entries(groupedMainData)) {
        if (groupName && groupName !== "undefined") {
          const roundIdentifierDetails = _.uniqBy(groupData.flatMap(
            (item) => item.roundIdentifierDetails
          ), "roundName");

          roundIdentifiersGroups[groupName.toLowerCase().split(" ").join("")] =
            tableViewValue.label === "Debt"
              ? roundIdentifierDetails.filter(
                  (round: any) =>
                    round.instrumentTypes.includes("NCD") ||
                    round.instrumentTypes.includes("OCD")
                )
              : tableViewValue.label === "Equity"
              ? roundIdentifierDetails.filter(
                  (round: any) =>
                    !round.instrumentTypes.includes("NCD") &&
                    !round.instrumentTypes.includes("OCD")
                )
              : roundIdentifierDetails;
          roundIdentifiersGroups[groupName.toLowerCase().split(" ").join("")] =
            getDisplayedRounds(
              roundIdentifiersGroups[
                groupName.toLowerCase().split(" ").join("")
              ],
              selectedRoundNames
            );
        }
      }

      rows.push({
        orgHierarchy: [getShareholderValue(shareholderType) || shareholderType],
        id: null,
        cin: cinNumber,
        roundIdentifiers,
        roundsTooltipField: [],
        shareholderName: { name: shareholderType, type: shareholderType },
        equityShares: totalEquityShares,
        tooltipEquity: totalEquityShares?.toLocaleString(currencyType),
        prefShares: totalPrefShares,
        tooltipPreference: totalPrefShares?.toLocaleString(currencyType),
        debentures: totalDebentures,
        tooltipDebentures: totalDebentures?.toLocaleString(currencyType),
        fdbShares: totalFdbShares,
        tooltipFdbShares: totalFdbShares?.toLocaleString(currencyType),
        fdbPercentage: totalFdbPercentage,
        tooltipFdbPercentage: totalFdbPercentage,
        warrants: totalWarrants,
        tooltipWarrants: totalWarrants?.toLocaleString(currencyType),
        notes: totalNotes,
        tooltipNotes: totalNotes?.toLocaleString(currencyType),
        options: totalOptions,
        tooltipOptions: totalOptions?.toLocaleString(currencyType),
        amountInvested: totalAmountInvested,
        tooltipAmountInvested:
          totalAmountInvested?.toLocaleString(currencyType),
        isPreferencePartlyPaid: typeData.some(
          (item: any) => item.isPreferencePartlyPaid
        ),
        isEquityPartlyPaid: typeData.some(
          (item: any) => item.isEquityPartlyPaid
        ),
        isDebtPartlyPaid: typeData.some((item: any) => item.isDebtPartlyPaid),
        currencySymbol,
        currencyType: currency,
        isdebt: typeData.some((item: any) => item.isDebt),
        totalAmount: totalTotalAmount,
        tooltipTotalAmountInvested:
          totalTotalAmount?.toLocaleString(currencyType),
      });

      const individuals = typeData.filter((item: any) => !item.groupName);
      const groups = _.groupBy(
        typeData.filter((item: any) => item.groupName),
        "groupName"
      );

      individuals.forEach((template: any) => {
        const filteredRoundData =
          tableViewValue.label === "Debt"
            ? template.roundIdentifierDetails.filter(
                (round: any) =>
                  round.instrumentTypes.includes("NCD") ||
                  round.instrumentTypes.includes("OCD")
              )
            : tableViewValue.label === "Equity"
            ? template.roundIdentifierDetails.filter(
                (round: any) =>
                  !round.instrumentTypes.includes("NCD") &&
                  !round.instrumentTypes.includes("OCD")
              )
            : template.roundIdentifierDetails;
        const displayedRoundData = getDisplayedRounds(
          filteredRoundData,
          selectedRoundNames
        );

        rows.push({
          orgHierarchy: [
            getShareholderValue(shareholderType) || shareholderType,
            `${template.name}_${template.id}`,
          ],
          id: template.id,
          cin: cinNumber,
          rounds: _.uniqBy(displayedRoundData, "roundName"),
          roundFilterValues: _.uniqBy(filteredRoundData, "roundName").map(
            (round: any) => round.roundName
          ),
          roundsTooltipField: getDisplayedRounds(
            template.roundIdentifierDetails,
            selectedRoundNames
          ),
          shareholderName: {
            name: template.name,
            type: getShareholderValue(shareholderType) || shareholderType,
          },
          equityShares: template.equityShares,
          tooltipEquity: template.equityShares?.toLocaleString(currencyType),
          prefShares: template.prefShares,
          tooltipPreference: template.prefShares?.toLocaleString(currencyType),
          debentures: template.debentures,
          tooltipDebentures: template.debentures?.toLocaleString(currencyType),
          fdbShares: template.fdbShares,
          tooltipFdbShares: template.fdbShares?.toLocaleString(currencyType),
          fdbPercentage: Number(Math.abs(template.fdbPercentage || 0) * 100),
          tooltipFdbPercentage: Math.abs(template.fdbPercentage || 0) * 100,
          warrants: template.warrants,
          tooltipWarrants: template.warrants?.toLocaleString(currencyType),
          notes: template.notes,
          tooltipNotes: template.notes?.toLocaleString(currencyType),
          options: template.options,
          tooltipOptions: template.options?.toLocaleString(currencyType),
          amountInvested: template.investedAmount,
          tooltipAmountInvested:
            template.investedAmount?.toLocaleString(currencyType),
          isPreferencePartlyPaid: template.isPreferencePartlyPaid,
          isEquityPartlyPaid: template.isEquityPartlyPaid,
          isDebtPartlyPaid: template.isDebtPartlyPaid,
          currencySymbol,
          currencyType: currency,
          isdebt: template.isDebt,
          totalAmount: template.totalAmount,
          tooltipTotalAmountInvested:
            template.totalAmount?.toLocaleString(currencyType),
        });
      });

      Object.entries(groups).forEach(([groupName, groupData]) => {
        const groupEquityShares = groupData.reduce(
          (acc: number, item: any) => acc + (item.equityShares || 0),
          0
        );
        const groupPrefShares = groupData.reduce(
          (acc: number, item: any) => acc + (item.prefShares || 0),
          0
        );
        const groupDebentures = groupData.reduce(
          (acc: number, item: any) => acc + (item.debentures || 0),
          0
        );
        const groupWarrants = groupData.reduce(
          (acc: number, item: any) => acc + (item.warrants || 0),
          0
        );
        const groupNotes = groupData.reduce(
          (acc: number, item: any) => acc + (item.notes || 0),
          0
        );
        const groupOptions = groupData.reduce(
          (acc: number, item:any) => acc + (item.notes || 0),
          0
        );
        const groupFdbShares = groupData.reduce(
          (acc: number, item: any) => acc + (item.fdbShares || 0),
          0
        );
        const groupAmountInvested = groupData.reduce(
          (acc: number, item: any) => acc + (item.investedAmount || 0),
          0
        );
        const groupFdbPercentage = groupData.reduce(
          (acc: number, item: any) =>
            acc + Math.abs(item.fdbPercentage || 0) * 100,
          0
        );
        const groupTotalAmount = groupData.reduce(
          (acc: number, item: any) => acc + (item.totalAmount || 0),
          0
        );

        rows.push({
          orgHierarchy: [
            getShareholderValue(shareholderType) || shareholderType,
            groupName,
          ],
          id: null,
          cin: cinNumber,
          rounds: [],
          isGroup: true,
          roundIdentifiers: roundIdentifiersGroups,
          roundsTooltipField: [],
          shareholderName: { name: groupName, type: shareholderType },
          groupName,
          equityShares: groupEquityShares,
          tooltipEquity: groupEquityShares?.toLocaleString(currencyType),
          prefShares: groupPrefShares,
          tooltipPreference: groupPrefShares?.toLocaleString(currencyType),
          debentures: groupDebentures,
          tooltipDebentures: groupDebentures?.toLocaleString(currencyType),
          fdbShares: groupFdbShares,
          tooltipFdbShares: groupFdbShares?.toLocaleString(currencyType),
          fdbPercentage: groupFdbPercentage,
          tooltipFdbPercentage: groupFdbPercentage,
          warrants: groupWarrants,
          tooltipWarrants: groupWarrants?.toLocaleString(currencyType),
          notes: groupNotes,
          tooltipNotes: groupNotes?.toLocaleString(currencyType),
          options: groupOptions,
          tooltipOptions: groupOptions?.toLocaleString(currencyType),
          amountInvested: groupAmountInvested,
          tooltipAmountInvested:
            groupAmountInvested?.toLocaleString(currencyType),
          isPreferencePartlyPaid: groupData.some(
            (item: any) => item.isPreferencePartlyPaid
          ),
          isEquityPartlyPaid: groupData.some(
            (item: any) => item.isEquityPartlyPaid
          ),
          isDebtPartlyPaid: groupData.some(
            (item: any) => item.isDebtPartlyPaid
          ),
          currencySymbol,
          currencyType: currency,
          isdebt: groupData.some((item: any) => item.isDebt),
          totalAmount: groupTotalAmount,
          tooltipTotalAmountInvested:
            groupTotalAmount?.toLocaleString(currencyType),
        });

        groupData.forEach((template: any) => {
          const filteredRoundData =
            tableViewValue.label === "Debt"
              ? template.roundIdentifierDetails.filter(
                  (round: any) =>
                    round.instrumentTypes.includes("NCD") ||
                    round.instrumentTypes.includes("OCD")
                )
              : tableViewValue.label === "Equity"
              ? template.roundIdentifierDetails.filter(
                  (round: any) =>
                    !round.instrumentTypes.includes("NCD") &&
                    !round.instrumentTypes.includes("OCD")
                )
              : template.roundIdentifierDetails;
          const displayedRoundData = getDisplayedRounds(
            filteredRoundData,
            selectedRoundNames
          );

          rows.push({
            orgHierarchy: [
              getShareholderValue(shareholderType) || shareholderType,
              groupName,
              `${template.name}_${template.id}`,
            ],
            id: template.id,
            cin: cinNumber,
            rounds: _.uniqBy(displayedRoundData, "roundName"),
            roundFilterValues: _.uniqBy(filteredRoundData, "roundName").map(
              (round: any) => round.roundName
            ),
            roundsTooltipField: getDisplayedRounds(
              template.roundIdentifierDetails,
              selectedRoundNames
            ),
            shareholderName: {
              name: template.name,
              type: getShareholderValue(shareholderType) || shareholderType,
            },
            groupName,
            equityShares: template.equityShares,
            tooltipEquity: template.equityShares?.toLocaleString(currencyType),
            prefShares: template.prefShares,
            tooltipPreference:
              template.prefShares?.toLocaleString(currencyType),
            debentures: template.debentures,
            tooltipDebentures:
              template.debentures?.toLocaleString(currencyType),
            fdbShares: template.fdbShares,
            tooltipFdbShares: template.fdbShares?.toLocaleString(currencyType),
            fdbPercentage: Number(Math.abs(template.fdbPercentage || 0) * 100),
            tooltipFdbPercentage: Math.abs(template.fdbPercentage || 0) * 100,
            warrants: template.warrants,
            tooltipWarrants: template.warrants?.toLocaleString(currencyType),
            notes: template.notes,
            tooltipNotes: template.notes?.toLocaleString(currencyType),
            options: template.options,
            tooltipOptions: template.options?.toLocaleString(currencyType),
            amountInvested: template.investedAmount,
            tooltipAmountInvested:
              template.investedAmount?.toLocaleString(currencyType),
            isPreferencePartlyPaid: template.isPreferencePartlyPaid,
            isEquityPartlyPaid: template.isEquityPartlyPaid,
            isDebtPartlyPaid: template.isDebtPartlyPaid,
            currencySymbol,
            currencyType: currency,
            isdebt: template.isDebt,
            totalAmount: template.totalAmount,
            tooltipTotalAmountInvested:
              template.totalAmount?.toLocaleString(currencyType),
          });
        });
      });
    });

    const filteredRows =
      tableViewValue.label === "Debt"
        ? rows.filter((row) => row.isdebt === true)
        : tableViewValue.label === "Equity"
        ? rows.filter((row) => row.fdbShares > 0)
        : rows;

    return filteredRows;
  }, [
    captableData,
    allEventsDetail,
    capTablefilterStore,
    groupDefaultExpanded,
    open,
    latestCaptable,
    tableViewValue,
    selectedRoundNames,
  ]);

  useEffect(() => {
    if (!gridApi.current?.api) return;
    gridApi.current.api.setRowData(rowData);
    gridApi.current.api.refreshCells({ force: true });
  }, [rowData]);

  function setTableHeight() {
    if (displayedRowCount === 1) {
      return (displayedRowCount + 2.75) * 60;
    } else if (displayedRowCount === 2) {
      return (displayedRowCount + 2.68) * 60;
    } else if (displayedRowCount === 3) {
      return (displayedRowCount + 3) * 60;
    } else if (displayedRowCount === 4) {
      return (displayedRowCount + 2.55) * 60;
    } else if (displayedRowCount === 5) {
      return (displayedRowCount + 2.48) * 60;
    } else if (displayedRowCount === 6) {
      return (displayedRowCount + 2.42) * 60;
    } else if (displayedRowCount === 7) {
      return (displayedRowCount + 2.35) * 60;
    } else if (displayedRowCount === 8) {
      return (displayedRowCount + 2.28) * 60;
    } else if (displayedRowCount === 9) {
      return (displayedRowCount + 2.22) * 60;
    } else {
      return 10 * 60;
    }
  }

  const handleCellClick = (cellParams: CellClickedEvent<any, any>) => {
    const template = cellParams.data;
    const isShareholderRow =
      (cellParams.node.level === 1 && !cellParams.data?.groupName) ||
      cellParams.node.level === 2;

    if (
      cellParams.column.getColId() === "ag-Grid-AutoColumn" &&
      isShareholderRow &&
      cellParams.value !== "Total"
    ) {
      onClickShareholderName(template.id);
    }
  };

  const [isColumnOpen, setIsColumnOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const openToolPanel = (key: any) => {
    if (key === "columns") {
      if (gridApi) {
        if (!isColumnOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsColumnOpen((state) => !state);
        setIsFilterOpen(false);
      }
    } else if (key === "filters") {
      if (gridApi) {
        if (!isFilterOpen) gridApi?.current?.api?.openToolPanel(key);
        else gridApi?.current?.api?.closeToolPanel();
        setIsFilterOpen((state) => !state);
        setIsColumnOpen(false);
      }
    }
  };

  function setPinnedBottomRowData({ api }: { api: any }) {
    const data = api.rowModel.rootNode;
    const filteredData = data.childrenAfterFilter.flatMap(
      (element: any) => element.childrenAfterFilter
    );

    const totalEquity = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.data.equityShares,
      0
    );

    const totalPreferenceShares = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.data.prefShares,
      0
    );

    const totalDebentures = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.data.debentures,
      0
    );

    const totalWarrants = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.data.warrants,
      0
    );

    const totalNotes = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.data.notes,
      0
    );
     const totalOptions = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.data.options,
      0
    );

    const totalFdbShares = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.data.fdbShares,
      0
    );

    const totalAmount = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.data.amountInvested,
      0
    );

    const totalPercentage = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + (data.data.fdbShares / totalFdbShares) * 100,
      0
    );

    const totalTotalAmount = filteredData?.reduce(
      (accumulator: any, data: any) => accumulator + data.data.totalAmount,
      0
    );

    api.setPinnedBottomRowData([
      {
        orgHierarchy: ["Total"],
        equityShares: totalEquity,
        tooltipEquity: totalEquity?.toLocaleString(currencyType),
        round: undefined,
        prefShares: totalPreferenceShares,
        tooltipPreference: totalPreferenceShares?.toLocaleString(currencyType),
        debentures: totalDebentures,
        tooltipDebentures: totalDebentures.toLocaleString(currencyType),
        notes: totalNotes,
        tooltipNotes: totalNotes.toLocaleString(currencyType),
        options: totalOptions,
        tooltipOptions: totalOptions.toLocaleString(currencyType),
        warrants: totalWarrants,
        tooltipWarrants: totalWarrants.toLocaleString(currencyType),
        amountInvested: totalAmount,
        tooltipAmountInvested: totalAmount.toLocaleString(currencyType),
        fdbShares: totalFdbShares,
        tooltipFdbShares: totalFdbShares.toLocaleString(currencyType),
        currencyType: currency,
        currency,
        fdbPercentage:
          Number(totalPercentage.toFixed(2)) > 99.98
            ? 100
            : totalPercentage.toFixed(2),
        tooltipFdbPercentage:
          Number(totalPercentage.toFixed(2)) > 99.98
            ? 100
            : totalPercentage.toFixed(2),
        roundsTooltipField: "87y980",
        totalAmount: totalTotalAmount,
        tooltipTotalAmountInvested:
          totalTotalAmount.toLocaleString(currencyType),
      },
    ]);
  }

  const [filteredRowData, setFilteredRowData] = useState<any>([]);

  const gridOptions = {
    suppressRowVirtualisation: true,
    paginationAutoPageSize: false,
    suppressScrollOnNewData: true,
    animateRows: true,
    getDataPath: (data: any) => data.orgHierarchy,
    isExpandable: (node: any) => node.data?.groupName || node.level === 0,
  };

  const filterDataList: any = [];
  const onAgGridFilterChanged = (grid: any) => {
    const filtersApplied = grid.api.isAnyFilterPresent();
    setIsFilterApplied(filtersApplied);
    const nextRoundNames = getSelectedRoundNames(grid.api.getFilterModel());
    setSelectedRoundNames((currentRoundNames) =>
      _.isEqual(currentRoundNames, nextRoundNames)
        ? currentRoundNames
        : nextRoundNames
    );
    const data = grid.api.getModel().rowsToDisplay;

    const traverseNodes = (parentNode: any) => {
      parentNode.childrenAfterFilter.forEach((node: any) => {
        node.childrenAfterFilter.forEach((node: any) =>
          filterDataList.push(node)
        );
      });
    };

    const model = grid.api.getModel();
    if (model && model.rootNode) {
      traverseNodes(model.rootNode);
    }

    setFilteredRowData(filterDataList);
    setPinnedBottomRowData(grid);
    uploadFilterAndColumn();
    uploadFilter();
  };

  useEffect(() => {
    const filterModel = gridApi.current?.api.getFilterModel();

    const filteredData = gridApi.current?.api
      .getModel()
      .rowsToDisplay.map((node: any) => node.data);

    setFilteredRowData(filteredData);
    gridApi.current?.api.setFilterModel(tableState?.filterState);
  }, [gridApi, tableState?.filterState, tableState?.columnState, captableData]);

  const CustomHeaderComponent = () => (
    <div>
      <HStack className="cursor-pointer" onClick={() => handleGroupToggle()}>
        <div className="pt-0.5">
          <ChevronDownIcon
            className={classNames(
              open ? "rotate-0" : "-rotate-90",
              "h-3 w-5 transform text-slate-500"
            )}
            strokeWidth={2.5}
          />
        </div>
        <span className="px-9">Group Name</span>
      </HStack>
    </div>
  );

  const handleGroupToggle = () => {
    if (open) {
      setOpen(false);
      setGroupDefaultExpanded(0);
    } else {
      setOpen(true);
      setGroupDefaultExpanded(-1);
    }
  };

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      minWidth: 300,
      menuTabs: [],
      cellRenderer: "agGroupCellRenderer",
      cellRendererParams: {
        innerRenderer: shareholderNameRender,
        suppressCount: true,
        enableCellClick: true,
      },
      sortable: true,
      valueGetter: (params) => {
        const value = params.data.orgHierarchy[params.data.orgHierarchy.length - 1];
        if (typeof value === 'string' && value.includes('_')) {
          const parts = value.split('_');
          return parts.slice(0, -1).join('_');
        }
        return value;
      },
      pinned: true,
      tooltipField: "tooltipEquity",
      headerComponent: CustomHeaderComponent,
      tooltipComponent: (params: any) => {
        if (
          params.data.orgHierarchy[0] !== "Total" &&
          (params.node.level === 1 || params.node.level === 2)
        ) {
          return <CustomToolTip {...params} />;
        }
        return null;
      },
      comparator: (a: any, b: any, nodeA: any, nodeB: any) => {
        const isTotalA = a === "Total";
        const isTotalB = b === "Total";

        if (isTotalA && !isTotalB) return 1;
        if (!isTotalA && isTotalB) return -1;

        const isGroupA = nodeA.data?.groupName && nodeA.level === 1;
        const isGroupB = nodeB.data?.groupName && nodeB.level === 1;

        if (nodeA.level === 1 && nodeB.level === 1) {
          if (!isGroupA && isGroupB) return -1;
          if (isGroupA && !isGroupB) return 1;
        }

        return (a || "").localeCompare(b || "");
      },
    }),
    [_captableData, groupDefaultExpanded, open]
  );

  const getDataPath = useMemo<GetDataPath>(
    () => (data: any) => data.orgHierarchy,
    []
  );

  return (
    <VStack
      className={`bg-white w-full border border-borderColor shadow-box rounded-lg ${
        isScenarioCaptable ? "" : "mt-8 shadow-box"
      }`}
    >
      {captableData.length > 0 && !isSubsidiary && allEventsDetail.length > 1 && (
        <div className="px-9 pt-2 mx-6 mt-6 border rounded-md shadow-box">
          <HStack className="justify-between">
            <p className="text-xxs">
              {allEventsDetail[0].roundSummary.name[0].toUpperCase() +
                allEventsDetail[0].roundSummary.name.slice(1)}
            </p>
            {allEventsDetail.length > 10 && (
              <p className="text-xxs">
                {allEventsDetail[
                  firstRoundMarkIndex
                ].roundSummary.name[0].toUpperCase() +
                  allEventsDetail[firstRoundMarkIndex].roundSummary.name.slice(
                    1
                  )}
              </p>
            )}
            {(allEventsDetail.length % 2 !== 0 ||
              allEventsDetail.length > 10) && (
              <p className="text-xxs">
                {allEventsDetail[
                  secondRoundMarkIndex
                ].roundSummary.name[0].toUpperCase() +
                  allEventsDetail[secondRoundMarkIndex].roundSummary.name.slice(
                    1
                  )}
              </p>
            )}
            {allEventsDetail.length > 10 && (
              <p className="text-xxs">
                {allEventsDetail[
                  thirdRoundMarkIndex
                ].roundSummary.name[0].toUpperCase() +
                  allEventsDetail[thirdRoundMarkIndex].roundSummary.name.slice(
                    1
                  )}
              </p>
            )}
            <p className="text-xxs">
              {allEventsDetail[
                allEventsDetail.length - 1
              ].roundSummary.name[0].toUpperCase() +
                allEventsDetail[
                  allEventsDetail.length - 1
                ].roundSummary.name.slice(1)}
            </p>
          </HStack>
          <Slider
            min={0}
            value={count}
            defaultValue={markLabels.length - 1}
            max={markLabels.length - 1}
            onChange={(e: any, countNumber: any) => {
              updateRange(countNumber);
            }}
            onChangeCommitted={(e: any, countNumber: any) => {
              if (allEventsDetail) {
                capTablefilterStore.setDateOfInvestment(
                  new Date(allEventsDetail[countNumber].eventDate)
                );
                capTablefilterStore.setRound(
                  allEventsDetail[countNumber].roundSummary.name
                );
              }
            }}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) =>
              allEventsDetail && (
                <div className="bg-transparent">
                  <p className="text-center">
                    {allEventsDetail[value]?.roundSummary?.name}
                  </p>
                  <p className="text-center">
                    (
                    {formatDisplayDate(
                      allEventsDetail[value]?.roundSummary?.date || new Date()
                    )}
                    )
                  </p>
                </div>
              )
            }
            marks={markLabels}
            sx={sliderStyle}
          />
        </div>
      )}

      <VStack className="min-h-full gap-4">
        <Dialog open={dialog.open} maxWidth="md">
          {dialog.type === "Export Summary Report" && (
            <ExportMultidateSheetDetails
              onPrimaryAction={() => setDialog({ open: false })}
              onSecondaryAction={() => setDialog({ open: false })}
              reportName={"Export Summary Report"}
            />
          )}
          {dialog.type !== "Export Summary Report" && (
            <ExportMultidateSheetDetails
              reportName={dialog.data || ""}
              companyId={companyId}
              onPrimaryAction={() => setDialog({ open: false })}
              onSecondaryAction={() => setDialog({ open: false })}
            />
          )}
        </Dialog>

        <VStack className="gap-8 bg-white rounded-lg">
          <HStack className="items-center justify-between gap-4 py-2">
            <GenericTableHeader
              heading={"Cap Table"}
              subHeading={"Shareholders"}
              count={
                isFilterApplied
                  ? filteredRowData.length ?? 0
                  : (tableViewValue.label === "Debt"
                      ? _captableData.filter(
                          (captable: any) => captable.isDebt === true
                        )
                      : tableViewValue.label === "Equity"
                      ? _captableData.filter(
                          (captable: any) => captable.fdbShares > 0
                        )
                      : _captableData
                    ).length ?? 0
              }
            />
            <HStack className="items-center justify-between w-full gap-4 px-2 py-2 lg:w-auto lg:justify-end">
              {!sharedPage && (
                <CloudSetting
                  tableId={`${TableId.dashboardCaptable}${companyId}web`}
                  isLoading={isSaving}
                  filterCondition={filterCondition}
                  setFilterCondition={setFilterCondition}
                />
              )}
              <div>
                <SearchInput
                  placeholder={`Search`}
                  onChange={(e: any) => {
                    gridApi.current.api.setQuickFilter(e.target.value);
                  }}
                />
              </div>
              <Select
                className="w-28"
                options={tableViewOptions}
                name="timelineField"
                value={tableViewValue}
                classNamePrefix="select2-selection"
                onChange={(e: any) => {
                  setTableViewValue(e);
                }}
              />
              <div className="hidden md:block ">
                {!cinNumber && (
                  <ExportImport
                    actions={
                      isScenarioCaptable
                        ? [
                            {
                              name: "Export Summary Report",
                            },
                          ]
                        : [
                            {
                              name: "Security Based Report",
                            },
                            {
                              name: "Round Based Report",
                              disabled: true,
                            },
                            {
                              name: "Export Summary Report",
                            },
                            {
                              name: "Allotment Window Report",
                            },
                            {
                              name: "Ownership Detailed Report",
                            },
                          ]
                    }
                    onAction={(action) => handleActions(action)}
                  />
                )}
              </div>
              <HStack className="items-center justify-end gap-4 py-2">
                <IconCTAButton
                  value={"Columns"}
                  onClick={() => openToolPanel("columns")}
                  iconName={"fluent:column-triple-edit-20-regular"}
                  className={`px-4 font-medium items-center flex flex-row ${
                    isColumnOpen ? "text-orange-501" : "text-gray-400"
                  }`}
                />
                <IconCTAButton
                  value={"Filters"}
                  onClick={() => openToolPanel("filters")}
                  iconName={"material-symbols:filter-alt"}
                  className={`px-4 mr-4 font-medium items-center flex flex-row ${
                    isFilterOpen ? "text-orange-501" : "text-gray-400"
                  }`}
                />
              </HStack>
            </HStack>
          </HStack>
          <HStack className="justify-between w-full">
            <Box
              style={{
                height: setTableHeight(),
              }}
              className="w-full h-full max-h-full overflow-x-auto ag-theme-material"
            >
              <AgGridReact
                sideBar={agConfigDashboardSideBar}
                ref={gridRef}
                onGridReady={(params) => {
                  gridApi.current = params;
                }}
                components={componentsRegistery}
                alwaysShowHorizontalScroll
                alwaysMultiSort
                animateRows={true}
                enableCharts={true}
                enableRangeSelection={true}
                defaultColDef={defaultColDef}
                onRowDataUpdated={setPinnedBottomRowData}
                onFilterChanged={onAgGridFilterChanged}
                onColumnEverythingChanged={uploadColumn}
                onColumnResized={uploadColumn}
                onColumnMoved={uploadColumn}
                gridOptions={gridOptions}
                rowData={rowData}
                onCellClicked={handleCellClick}
                columnDefs={columnDefs}
                autoGroupColumnDef={autoGroupColumnDef}
                treeData={true}
                groupDefaultExpanded={groupDefaultExpanded}
                pagination={false}
                getDataPath={getDataPath}
                suppressRowTransform={true}
                suppressCopyRowsToClipboard={true}
                suppressCopySingleCellRanges={true}
                suppressCellFocus={true}
                suppressMenuHide={false}
                tooltipShowDelay={1000}
                tooltipInteraction={true}
                rowClass={"border-t border-dashed "}
                overlayNoRowsTemplate={
                  '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow; margin-top: 50px;">No Rows To Show</span>'
                }
              ></AgGridReact>
            </Box>
          </HStack>
        </VStack>
      </VStack>
    </VStack>
  );
}

export default AgGridDashboardCaptable;
